import {
  List, Edit, Create, SimpleForm,
  TextInput, BooleanInput, NumberInput, required,
} from 'react-admin';
import { Stack, Box, Typography } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { ImageUploadInput } from '../../components/ImageUploadInput.jsx';
import { ReorderableDatagrid, imageCell } from '../../components/ReorderableDatagrid.jsx';
import { SpecsEditor } from '../../components/SpecsEditor.jsx';

const filters = [
  <TextInput key="q" source="q" label="Search title/eyebrow" alwaysOn />,
];

const FacilityList = () => (
  <List
    sort={{ field: 'sortOrder', order: 'ASC' }}
    perPage={100}
    pagination={false}
    filters={filters}
    empty={false}
  >
    <ReorderableDatagrid
      columns={[
        { key: 'image',    header: 'Image',   width: 110, render: (r) => imageCell(r.image) },
        { key: 'title',    header: 'Title',               render: (r) => r.title },
        { key: 'eyebrow',  header: 'Eyebrow', width: 160, render: (r) => r.eyebrow || '—' },
        { key: 'isActive', header: 'Active',  width: 80,  render: (r) => r.isActive ? '✓' : '—' },
      ]}
    />
  </List>
);

const FacilityForm = () => (
  <Stack spacing={2} sx={{ maxWidth: 760 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextInput
        source="title"
        label="Title"
        validate={required()}
        helperText='The last word will be italicised on the public page. E.g. "Science Labs" → "Science <em>Labs</em>".'
        sx={{ flex: 1 }}
      />
      <NumberInput source="sortOrder" defaultValue={0} sx={{ width: 140 }} />
      <BooleanInput source="isActive" label="Active" defaultValue={true} />
    </Stack>

    <TextInput
      source="eyebrow"
      label="Eyebrow"
      helperText='Small caps category label above the title. E.g. "Spaces", "Active life", "Performance".'
      fullWidth
    />

    <ImageUploadInput source="image" label="Facility image" />

    <TextInput
      source="lede"
      label="Lede (intro paragraph)"
      multiline
      minRows={2}
      fullWidth
      helperText="A short, punchier opening sentence."
    />

    <TextInput
      source="body"
      label="Body"
      multiline
      minRows={3}
      fullWidth
      helperText="A longer description paragraph below the lede."
    />

    <SpecsEditor source="specs" label="Highlights" />

    <Box sx={{ background: 'var(--cream, #faf6ec)', border: '1px dashed var(--line, #e6e1d4)', borderRadius: 1, p: 1.5, mt: 1 }}>
      <Typography variant="caption" sx={{ color: 'var(--ink-3, #5d6660)' }}>
        <strong>Layout:</strong> the public facilities page lists facilities in <code>sortOrder</code>, alternating image-left / image-right. Inactive facilities are hidden from the public site.
      </Typography>
    </Box>
  </Stack>
);

const FacilityEdit = () => (
  <Edit redirect="list" mutationMode="pessimistic">
    <SimpleForm><FacilityForm /></SimpleForm>
  </Edit>
);

const FacilityCreate = () => (
  <Create redirect="list">
    <SimpleForm><FacilityForm /></SimpleForm>
  </Create>
);

export default {
  list: FacilityList,
  edit: FacilityEdit,
  create: FacilityCreate,
  icon: ApartmentIcon,
  options: { label: 'Facilities' },
  recordRepresentation: 'title',
};
